import useProductAutocomplete from '@/hooks/components/useAutoCompleteHook';
import { ProductOption } from '@/types/pages/device';
import { AutocompleteRenderInputParams, LinearProgress, Typography, Paper } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import BaseAutocomplete from '../form/BaseAutocomplete';
type BaseAutoCompleteInputProps = {
    renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
    selectedProduct: ProductOption | null;
    setSelectedProduct: (prev: ProductOption | null) => void;
};
const BaseAutoCompleteInput = ({ renderInput, selectedProduct, setSelectedProduct }: BaseAutoCompleteInputProps) => {
    const { t } = useTranslation('common');
    const { options, loading, handleInputChange } = useProductAutocomplete();

    return (
        <BaseAutocomplete
            freeSolo
            forcePopupIcon
            value={selectedProduct}
            options={options}
            loading={loading}
            filterOptions={(options, { inputValue }) => {
                const lowerInput = inputValue.toLowerCase();
                return options.filter((option) => {
                    const nameLower = option.name.toLowerCase();
                    const barcodeLower = option.barcode.toLowerCase();
                    const number = option.number.toLowerCase();

                    return (
                        nameLower.includes(lowerInput) ||
                        barcodeLower.includes(lowerInput) ||
                        number.includes(lowerInput)
                    );
                });
            }}
            getOptionLabel={(option) =>
                typeof option === 'string' ? option : `${option.number} - ${option.name} (${option.barcode})`
            }
            onInputChange={(_, newValue) => {
                handleInputChange(newValue);
            }}
            onChange={(_, newValue) => {
                if (typeof newValue !== 'string') {
                    setSelectedProduct(newValue);
                }
            }}
            loadingText={
                <>
                    <LinearProgress />
                    <Typography sx={{ ml: 0.5, mt: 1.5 }}>{t('Loading Products...')}</Typography>
                </>
            }
            noOptionsText={t('No product data available.')}
            renderInput={renderInput}
            renderOption={(props, option) => (
                <li {...props} key={option.id}>
                    {option.number} - {option.name} ({option.barcode})
                </li>
            )}
            slots={{
                paper: (props) => (
                    <Paper
                        sx={{
                            maxHeight: '30vh',
                            overflowY: 'auto',
                        }}
                        {...props}
                    />
                ),
            }}
        />
    );
};

export default BaseAutoCompleteInput;
